/* Setup / Rules "Download CSV": pure column maps over the tab's view rows + the paged loaders. */
import { collect_all_pages, next_offset } from '../../../util_components/csv_export/collect_pages';
import { csv_file_name } from '../../../util_components/csv_export/to_csv';
import {
  SEED_EFFECTIVE_DATE,
  SYSTEM_OPENING_DATE,
  approval_reason_text,
  change_rules_text,
  change_status_view,
  line_label,
  month_text,
  preview_amount,
  version_status_view
} from './logic';

// A changed-by name falls back to an email server-side; an address never goes in a CSV.
const no_email = (s) => (/@/.test(String(s || '')) ? '' : s || '');
const name_of = (who) => (who && who.name) || '';

const VERSION_COLUMNS = [
  { header: 'Rule', key: 'name' },
  { header: 'Rule key', key: 'rule_key' },
  { header: 'Version', key: 'version_no', kind: 'number' },
  { header: 'Version ID', key: 'version_id' },
  { header: 'Condition', key: 'sentence' },
  { header: 'Teachers read', key: 'teacher_title' },
  { header: 'Teachers read (why)', key: 'teacher_why' },
  { header: 'Status', value: (r) => version_status_view(r.status).label },
  { header: 'On', key: 'active', kind: 'bool' },
  // The seed's placeholder date reads as the system opening date, as on screen.
  {
    header: 'Effective from',
    value: (r) =>
      r.effective_date === SEED_EFFECTIVE_DATE ? SYSTEM_OPENING_DATE : r.effective_date,
    kind: 'date'
  },
  { header: 'Changed by', value: (r) => (r.is_seed ? 'Seeded' : no_email(r.changed_by)) },
  { header: 'Changed at (IST)', key: 'changed_at_ist', kind: 'datetime' },
  { header: 'Note', key: 'note' }
];

// Rules in force: one row per rule across every stage/group card the filters leave on screen.
export const RULE_CSV_COLUMNS = [
  { header: 'Stage', key: 'stage_label' },
  { header: 'Group', key: 'group_name' },
  ...VERSION_COLUMNS
];

export const rule_csv_rows = (stages) =>
  (stages || []).flatMap((s) =>
    s.groups.flatMap((g) =>
      g.rules.map((r) => ({ ...r, stage_label: s.label, group_name: g.name }))
    )
  );

export const SCHEDULED_CSV_COLUMNS = VERSION_COLUMNS;
export const HISTORY_CSV_COLUMNS = VERSION_COLUMNS;

// Waiting for approval: wire rows (queue_view), worded like approval_row.
export const approval_csv_columns = (names) => [
  { header: 'Change ID', key: 'change_uuid' },
  { header: 'Rules', value: (x) => change_rules_text(x, names) },
  { header: 'Takes effect', key: 'effective_date', kind: 'date' },
  { header: 'Asked by', value: (x) => name_of((x.approval || {}).requested_by) },
  {
    header: 'Asked at (IST)',
    value: (x) => (x.approval || {}).requested_at_ist,
    kind: 'datetime'
  },
  {
    header: 'Needs approval because',
    value: (x) => ((x.approval || {}).reasons || []).map(approval_reason_text)
  },
  { header: 'Teachers', value: (x) => (x.impact ? x.impact.teachers_changed : ''), kind: 'number' },
  { header: 'Rs moved', value: (x) => (x.impact ? x.impact.abs_delta : ''), kind: 'money' },
  { header: 'Status', value: (x) => change_status_view(x.status).label },
  { header: 'Blocked', value: (x) => (x.blocker ? x.blocker.message || x.blocker.code : '') }
];

// Rule changes log: wire rows, every page.
export const change_csv_columns = (names) => [
  { header: 'Change ID', key: 'change_uuid' },
  { header: 'Prepared at (IST)', key: 'created_at_ist', kind: 'datetime' },
  { header: 'Prepared by', value: (c) => name_of(c.requested_by) },
  { header: 'Rules', value: (c) => change_rules_text(c, names) },
  { header: 'Takes effect', key: 'effective_date', kind: 'date' },
  { header: 'Status', value: (c) => change_status_view(c.status).label },
  { header: 'Saved at (IST)', key: 'applied_at_ist', kind: 'datetime' },
  { header: 'Why', key: 'note' }
];

export const CHANGES_PAGE_MAX = 200; // GET /rules/changes refuses a limit above 200

export const changes_csv_loader = (fetch_changes) => () =>
  collect_all_pages((before) =>
    fetch_changes(before || 0, CHANGES_PAGE_MAX).then((data) => ({
      rows: (data && Array.isArray(data.changes) && data.changes) || [],
      next: (data && data.next_before_id) || null
    }))
  );

// Preview lines: one row per changed line of each teacher-month (a month with no line detail still lists).
export const PREVIEW_LINE_CSV_COLUMNS = [
  { header: 'Teacher', key: 'teacher_name' },
  { header: 'Teacher UUID', key: 'teacher_uuid' },
  { header: 'Month', key: 'month' },
  { header: 'Payable before', key: 'old_payable', kind: 'money' },
  { header: 'Payable after', key: 'new_payable', kind: 'money' },
  { header: 'Change', key: 'delta', kind: 'money' },
  { header: 'Class ID', key: 'session_id' },
  { header: 'Session UUID', key: 'session_uuid' },
  { header: 'Line', key: 'line_label' },
  { header: 'Line before', key: 'line_old', kind: 'money' },
  { header: 'Line after', key: 'line_new', kind: 'money' }
];

export const preview_line_csv_rows = (wire_rows, cat) =>
  (wire_rows || []).flatMap((r) => {
    const base = {
      teacher_name: r.teacher_name || `Teacher ID ${r.teacher_id}`,
      teacher_uuid: r.teacher_uuid || '',
      month: month_text(r.pay_month),
      old_payable: preview_amount(r.old_payable_paise).value,
      new_payable: preview_amount(r.new_payable_paise).value,
      delta: preview_amount(r.delta_paise, r.delta).value
    };
    const lines = Array.isArray(r.lines) ? r.lines : [];
    if (!lines.length) return [base];
    return lines.map((l) => ({
      ...base,
      session_id: l.session_id ? String(l.session_id) : '',
      // blank on a month-level line, or a preview stored before the server sent it
      session_uuid: l.session_uuid || '',
      line_label: line_label(l.line, cat),
      line_old: l.old,
      line_new: l.new
    }));
  });

export const PREVIEW_LINES_PAGE_MAX = 500; // GET /rules/preview/lines refuses a limit above 500

export const preview_lines_csv_loader = (fetch_lines, preview_uuid, cat) => () =>
  collect_all_pages((offset) => {
    const at = offset || 0;
    return fetch_lines(preview_uuid, PREVIEW_LINES_PAGE_MAX, at).then((data) => {
      const page = (data && Array.isArray(data.rows) && data.rows) || [];
      return {
        rows: page,
        next: next_offset({
          offset: at,
          page_size: PREVIEW_LINES_PAGE_MAX,
          row_count: page.length,
          total_count: data && typeof data.total === 'number' ? data.total : undefined
        })
      };
    });
  }).then((res) => ({ ...res, rows: preview_line_csv_rows(res.rows, cat) }));

// Preview summary tables (summary_view rows).
export const IMPACT_TYPE_CSV_COLUMNS = [
  { header: 'Line', key: 'label' },
  { header: 'Code', key: 'key' },
  { header: 'Added', key: 'added', kind: 'number' },
  { header: 'Removed', key: 'removed', kind: 'number' },
  { header: 'Changed', key: 'changed', kind: 'number' },
  { header: 'Money', key: 'delta', kind: 'money' }
];

export const RULE_MATCH_CSV_COLUMNS = [
  { header: 'Rule', key: 'name' },
  { header: 'Rule key', key: 'key' },
  { header: 'Before', key: 'sessions_before', kind: 'number' },
  { header: 'After', key: 'sessions_after', kind: 'number' },
  { header: 'Matched no class', key: 'matches_nothing', kind: 'bool' }
];

export const rules_csv_name = (table, period = '') =>
  csv_file_name({ page: 'rules', table, period });
